import React, { useState, useEffect } from 'react';
import { Card, Tag, Button, Avatar, message, Typography, Space, Modal, Row, Col, Grid } from 'antd';
import { UserOutlined, ReloadOutlined, RollbackOutlined, ExclamationCircleOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { apiGetAllUser, apiUpdateStatusUser } from '../services/authService';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const DEPARTMENTS = ['Máy tính', 'Công trình', 'Dự án', 'Photocopy'];

const DEPT_COLOR = {
    'Máy tính': 'blue',
    'Công trình': 'orange',
    'Dự án': 'green',
    'Photocopy': 'purple',
};

const STATUS_INFO = {
    0:    { text: 'Sẵn sàng',           color: 'success', tagColor: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
    '-1': { text: 'Có việc chưa nhận',  color: 'warning', tagColor: '#faad14', bg: '#fffbe6', border: '#ffe58f' },
    1:    { text: 'Đang làm việc',       color: 'error',   tagColor: '#ff4d4f', bg: '#fff2f0', border: '#ffccc7' },
};

const getStatusInfo = (status) =>
    STATUS_INFO[status] ?? { text: 'Không xác định', color: 'default', tagColor: '#999', bg: '#f5f5f5', border: '#d9d9d9' };

const ResetStatusComponent = () => {
    const [users, setUsers]           = useState([]);
    const [loading, setLoading]       = useState(false);
    const [resettingId, setResettingId] = useState(null);

    const screens  = useBreakpoint();
    const isMobile = !screens.md; // dùng để thu gọn header title

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await apiGetAllUser();
            let arr = [];
            if (response?.data && Array.isArray(response.data)) arr = response.data;
            else if (Array.isArray(response)) arr = response;
            setUsers(arr.filter(u => DEPARTMENTS.includes(u.part) || DEPARTMENTS.includes(u.department)));
        } catch {
            message.error('Không thể tải danh sách nhân viên');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchUsers(); }, []);

    const doReset = async (user) => {
        setResettingId(user.id);
        try {
            await apiUpdateStatusUser(user.id, 0);
            setUsers(prev => prev.map(u => u.id === user.id ? { ...u, status: 0 } : u));
            message.success(`Đã reset ${user.username} về sẵn sàng`);
        } catch {
            message.error(`Lỗi khi reset ${user.username}`);
        } finally {
            setResettingId(null);
        }
    };

    const handleReset = (user) => {
        const info = getStatusInfo(user.status);
        Modal.confirm({
            title: 'Xác nhận reset trạng thái',
            icon: <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />,
            content: (
                <div>
                    <p>Reset trạng thái của <strong>{user.username}</strong>?</p>
                    <div style={{ padding: '8px 12px', background: '#fff7e6', borderRadius: 6, border: '1px solid #ffd591' }}>
                        <div>Hiện tại: <Tag color={info.color}>{info.text}</Tag></div>
                        <div style={{ marginTop: 4 }}>Sau reset: <Tag color="success">Sẵn sàng</Tag></div>
                    </div>
                </div>
            ),
            okText: 'Xác nhận Reset',
            okType: 'primary',
            cancelText: 'Hủy',
            onOk: () => doReset(user),
        });
    };

    const readyUsers = users.filter(u => u.status === 0);
    const stuckUsers = users.filter(u => u.status !== 0);
    const stuckCount = stuckUsers.length;
    const readyCount = readyUsers.length;

    // ── Card (dùng chung mobile + desktop) ───────────────────────────────────
    const renderCard = (user) => {
        const info    = getStatusInfo(user.status);
        const isReady = user.status === 0;
        const dept    = user.part || user.department || 'N/A';

        return (
            <div style={{
                border: `2px solid ${info.border}`,
                borderRadius: 10,
                background: info.bg,
                padding: '12px 14px',
                height: '100%',
            }}>
                {/* Row trên: avatar + tên + bộ phận */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <Avatar size={40} src={user.avatar} icon={<UserOutlined />} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <Text strong style={{ fontSize: 15 }}>{user.username}</Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>ID: {user.id}</Text>
                        </div>
                        <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <Tag color={DEPT_COLOR[dept] || 'default'} style={{ margin: 0, fontSize: 11 }}>{dept}</Tag>
                            <Tag color={info.color} style={{ margin: 0, fontSize: 11 }}>{info.text}</Tag>
                        </div>
                    </div>
                </div>

                {/* Nút reset full-width */}
                <Button
                    block
                    icon={isReady ? <CheckCircleOutlined /> : <RollbackOutlined />}
                    disabled={isReady}
                    loading={resettingId === user.id}
                    onClick={() => handleReset(user)}
                    style={isReady
                        ? { background: '#f6ffed', borderColor: '#b7eb8f', color: '#52c41a', fontWeight: 600 }
                        : { background: '#fa8c16', borderColor: '#fa8c16', color: '#fff', fontWeight: 600, fontSize: 14, height: 38 }
                    }
                >
                    {isReady ? 'Đã sẵn sàng' : 'Reset về sẵn sàng'}
                </Button>
            </div>
        );
    };

    // ── Cột (Đã sẵn sàng / Có thể reset) ─────────────────────────────────────
    const renderColumn = (title, icon, color, bg, border, list) => (
        <Col xs={24} lg={12}>
            <div style={{
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: 8,
                padding: '8px 14px',
                marginBottom: 12,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Space size={6}>
                    {icon}
                    <Text strong style={{ color, fontSize: 14 }}>{title}</Text>
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>{list.length} người</Text>
            </div>
            {list.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Không có nhân viên</Text>
                </div>
            ) : (
                <Row gutter={[12, 12]}>
                    {list.map(user => (
                        <Col xs={24} sm={12} key={user.id}>
                            {renderCard(user)}
                        </Col>
                    ))}
                </Row>
            )}
        </Col>
    );

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div style={{ padding: isMobile ? 16 : 24 }}>
            <Title level={4} style={{ margin: '0 0 16px' }}>Reset trạng thái nhân viên</Title>

            {/* Header card */}
            <Card
                bordered={false}
                style={{ marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
                styles={{ body: { padding: '14px 20px' } }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <Space size={8}>
                        <Tag icon={<WarningOutlined />} color="warning" style={{ margin: 0, padding: '2px 10px' }}>
                            {stuckCount} bị kẹt
                        </Tag>
                        <Tag icon={<CheckCircleOutlined />} color="success" style={{ margin: 0, padding: '2px 10px' }}>
                            {readyCount} sẵn sàng
                        </Tag>
                    </Space>
                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={fetchUsers}
                        loading={loading}
                    >
                        Tải lại
                    </Button>
                </div>
            </Card>

            {/* Nội dung */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                    <Text type="secondary">Đang tải...</Text>
                </div>
            ) : users.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                    <Text type="secondary">Không có nhân viên nào</Text>
                </div>
            ) : (
                <Row gutter={[16, 16]}>
                    {renderColumn('Đã sẵn sàng', <CheckCircleOutlined style={{ color: '#52c41a' }} />, '#389e0d', '#f6ffed', '#b7eb8f', readyUsers)}
                    {renderColumn('Có thể reset', <WarningOutlined style={{ color: '#fa8c16' }} />, '#d46b08', '#fff7e6', '#ffd591', stuckUsers)}
                </Row>
            )}
        </div>
    );
};

export default ResetStatusComponent;
